const imported_stylus_components = require('.cache/react-style-loader/src/pages/TaskDefinition/components/TaskPipeline.styl');
import ELK from 'elkjs/lib/elk.bundled';
import { Icon, Spinner } from '@blueprintjs/core';
import {
    Background,
    ControlButton,
    Controls,
    MarkerType,
    Position,
    ReactFlow,
    ReactFlowProvider,
    useReactFlow,
} from 'reactflow';
import TaskTypeIcon from 'src/pages/Tasks/components/TaskTypeIcon';
import { type DependencyList, useEffect, useMemo, useState } from 'react';

import useTaskTree from '../hooks/useTaskTree';
import { NodeTags } from './TaskTree';

import type { TaskTreeNode } from '../hooks/useTaskTree';
import type { ClassAndStyle } from '@pi/ui';

export interface TaskPipelineProps extends ClassAndStyle {
    taskId: string;
    onMaximizedChanged?: (maximized: boolean) => void;
    canMaximize?: boolean;
}

const MINIMIZED_HEIGHT = '20rem';

const TaskPipeline: React.FC<TaskPipelineProps> = ({ taskId, ...props }) => {
    const { loading, root } = useTaskTree(taskId);

    const preLayoutNodesAndEdges = useMemo(() => buildNodesAndEdges(root), [root]);
    const elkNodesAndEdges = useElkLayout(preLayoutNodesAndEdges);

    if (loading) return <Spinner />;

    if (!elkNodesAndEdges?.edges?.length) return null;

    return (
        <ReactFlowProvider>
            <ReactFlowContent {...props} {...elkNodesAndEdges} />
        </ReactFlowProvider>
    );
};

const ReactFlowContent: React.FC<
    FlowProps & { onMaximizedChanged?: (maximized: boolean) => void; canMaximize?: boolean }
> = ({ nodes, edges, onMaximizedChanged, canMaximize = true, ...props }) => {
    const [maximized, setMaximized] = useState(false);
    const [height, setHeight] = useState(MINIMIZED_HEIGHT);
    const reactFlowInstance = useReactFlow();

    useEffect(() => {
        onMaximizedChanged?.(maximized);
        setHeight(maximized ? `${window.innerHeight - 100}px` : MINIMIZED_HEIGHT);
    }, [maximized, onMaximizedChanged]);

    useEffect(() => {
        void Promise.sleep(20).then(() => {
            reactFlowInstance.fitView();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [height]);

    const { style, className, ...reactFlowProps } = props;

    return (
        <div style={{ ...style, height }} className={className}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodesDraggable={false}
                nodesConnectable={false}
                fitView
                {...reactFlowProps}
            >
                <Controls showInteractive={false}>
                    {canMaximize && (
                        <ControlButton onClick={() => setMaximized(m => !m)}>
                            <Icon icon={maximized ? 'minimize' : 'maximize'} color='#111418' />
                        </ControlButton>
                    )}
                </Controls>
                <Background />
            </ReactFlow>
        </div>
    );
};

export default TaskPipeline;

type BaseFlowProps = React.ComponentProps<typeof ReactFlow>;

interface FlowProps extends BaseFlowProps {
    nodes: NonNullable<BaseFlowProps['nodes']>;
    edges: NonNullable<BaseFlowProps['edges']>;
}

const useElkLayout = (input: FlowProps): FlowProps => {
    const [layoutResult, setLayoutResult] = useState<FlowProps>({ nodes: [], edges: [] });

    const elk = useMemo(() => new ELK(), []);

    const isHorizontal = true;

    useAsyncEffect(async () => {
        const elkResult = await elk.layout({
            id: 'root',
            edges: input.edges as any,
            layoutOptions: {
                'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
                'elk.layered.crossingMinimization.forceNodeModelOrder': 'true',
                'spacing.baseValue': '35',
                'elk.layered.unnecessaryBendpoints': 'true',
                'elk.spacing.nodeNode': '100',
                'elk.direction': 'RIGHT',
            },
            children: input.nodes.map(node => ({
                ...node,
                targetPosition: isHorizontal ? 'left' : 'top',
                sourcePosition: isHorizontal ? 'right' : 'bottom',
                width: 150,
                height: 50,
            })),
        });

        if (elkResult.children) {
            setLayoutResult({
                edges: elkResult.edges as any,
                nodes: elkResult.children.map(node => ({
                    ...node,
                    position: { x: node.x, y: node.y },
                })) as any,
            });
        }
    }, [input, elk, isHorizontal]);

    return layoutResult;
};

const useAsyncEffect = (effect: () => Promise<void>, deps?: DependencyList) => {
    useEffect(() => {
        void effect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
};

const buildNodesAndEdges = (root: TaskTreeNode | undefined) => {
    const props: FlowProps = {
        nodes: [],
        edges: [],
    };

    if (!root) return props;

    let minY = 0;

    const iterate = (
        node: TaskTreeNode,
        pos: { x: number; y: number } = { x: 0, y: 0 },
        maxSiblingDepth = 0,
    ) => {
        pos = { ...pos };
        minY = Math.min(minY, pos.y);

        if (node.dependencies?.length) {
            node.dependencies.forEach(dep => {
                props.edges.push({
                    id: node.id + '__' + dep.id,
                    source: dep.id,
                    target: node.id,
                    style: {
                        stroke: 'red',
                    },
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        width: 20,
                        height: 20,
                        color: 'red',
                    },
                });
            });
        }

        if (node.children?.length) {
            node.children.forEach((child, index) => {
                props.edges.push({
                    id: node.id + '__' + child.id,
                    source: node.id,
                    target: child.id,
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        width: 20,
                        height: 20,
                    },
                });

                iterate(
                    child,
                    {
                        x: pos.x + 1 + maxSiblingDepth + child.dependencyDepth,
                        y: pos.y + -1 * Math.floor(node.children.length / 2) + index,
                    },
                    node.children.map(c => c.dependencyDepth).reduce((a, b) => (a > b ? a : b), 0),
                );
            });
        }

        props.nodes.push({
            id: node.id,
            position: {
                x: pos.x,
                y: pos.y,
            },
            data: {
                label: (
                    <>
                        <Row>
                            <TaskTypeIcon type={node.task.type} enabled={node.task.enabled} />
                            <div style={{ flex: '1 1 0' }}>
                                <div>{node.task.name}</div>
                                <NodeTags node={node} />
                            </div>
                            <TypeLabel>{node.task.type}</TypeLabel>
                        </Row>
                    </>
                ),
            },
            sourcePosition: Position.Right,
            targetPosition: Position.Left,
            style: {
                background: 'var(--menu-background-color)',
                color: 'var(--bp-pt-text-color)',
            },
        });
    };

    iterate(root);

    for (const node of props.nodes) {
        node.position.x *= 230;
        node.position.y = 50 + (minY * -1 + node.position.y) * 120;
    }

    return props;
};

const Row = imported_stylus_components.Row;
const TypeLabel = imported_stylus_components.TypeLabel;